/**
 * Environment Management API Client
 */

import { api } from '@/lib/api-client';
import type {
  Environment,
  CreateEnvironmentInput,
  UpdateEnvironmentInput,
  EnvironmentResponse,
  EnvironmentListResponse,
  EnvironmentSecret,
  SecretListResponse,
  AgentDeployment,
  CreateDeploymentInput,
  DeploymentResponse,
  DeploymentListResponse,
  ApprovalResponse,
  DeploymentHistoryResponse,
  DeploymentDiffResponse,
} from './types';

const ENVIRONMENTS_BASE = '/api/v1/environments';
const DEPLOYMENTS_BASE = '/api/v1/deployments';

// ============================================
// Environments
// ============================================

/**
 * List environments
 */
export async function listEnvironments(params?: {
  type?: string;
  status?: string;
}): Promise<EnvironmentListResponse> {
  return api.get<EnvironmentListResponse>(ENVIRONMENTS_BASE, { params: params as Record<string, string> });
}

/**
 * Get environment by ID
 */
export async function getEnvironment(id: string): Promise<EnvironmentResponse> {
  return api.get<EnvironmentResponse>(`${ENVIRONMENTS_BASE}/${id}`);
}

/**
 * Create environment
 */
export async function createEnvironment(input: CreateEnvironmentInput): Promise<EnvironmentResponse> {
  return api.post<EnvironmentResponse>(ENVIRONMENTS_BASE, input);
}

/**
 * Update environment
 */
export async function updateEnvironment(
  id: string,
  input: UpdateEnvironmentInput
): Promise<EnvironmentResponse> {
  return api.put<EnvironmentResponse>(`${ENVIRONMENTS_BASE}/${id}`, input);
}

/**
 * Delete environment
 */
export async function deleteEnvironment(id: string): Promise<{ success: boolean }> {
  return api.delete(`${ENVIRONMENTS_BASE}/${id}`);
}

// ============================================
// Environment Secrets
// ============================================

/**
 * List secrets for an environment
 */
export async function listSecrets(environmentId: string): Promise<SecretListResponse> {
  return api.get<SecretListResponse>(`${ENVIRONMENTS_BASE}/${environmentId}/secrets`);
}

/**
 * Set a secret
 */
export async function setSecret(
  environmentId: string,
  key: string,
  value: string,
  description?: string
): Promise<{ success: boolean; data: EnvironmentSecret }> {
  return api.post(`${ENVIRONMENTS_BASE}/${environmentId}/secrets`, {
    key,
    value,
    description,
  });
}

/**
 * Delete a secret
 */
export async function deleteSecret(
  environmentId: string,
  key: string
): Promise<{ success: boolean }> {
  return api.delete(`${ENVIRONMENTS_BASE}/${environmentId}/secrets/${encodeURIComponent(key)}`);
}

// ============================================
// Deployments
// ============================================

/**
 * List deployments
 */
export async function listDeployments(params?: {
  agent_id?: string;
  environment_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<DeploymentListResponse> {
  const queryParams: Record<string, string> = {};
  if (params?.agent_id) queryParams.agent_id = params.agent_id;
  if (params?.environment_id) queryParams.environment_id = params.environment_id;
  if (params?.status) queryParams.status = params.status;
  if (params?.limit) queryParams.limit = String(params.limit);
  if (params?.offset) queryParams.offset = String(params.offset);

  return api.get<DeploymentListResponse>(DEPLOYMENTS_BASE, { params: queryParams });
}

/**
 * Get deployment by ID
 */
export async function getDeployment(id: string): Promise<DeploymentResponse> {
  return api.get<DeploymentResponse>(`${DEPLOYMENTS_BASE}/${id}`);
}

/**
 * Create a new deployment
 */
export async function createDeployment(input: CreateDeploymentInput): Promise<DeploymentResponse> {
  return api.post<DeploymentResponse>(DEPLOYMENTS_BASE, input);
}

/**
 * Approve a deployment
 */
export async function approveDeployment(
  id: string,
  comment?: string
): Promise<ApprovalResponse> {
  return api.post<ApprovalResponse>(`${DEPLOYMENTS_BASE}/${id}/approve`, { comment });
}

/**
 * Reject a deployment
 */
export async function rejectDeployment(
  id: string,
  comment: string
): Promise<ApprovalResponse> {
  return api.post<ApprovalResponse>(`${DEPLOYMENTS_BASE}/${id}/reject`, { comment });
}

/**
 * Rollback a deployment
 */
export async function rollbackDeployment(
  id: string,
  reason: string
): Promise<DeploymentResponse> {
  return api.post<DeploymentResponse>(`${DEPLOYMENTS_BASE}/${id}/rollback`, { reason });
}

/**
 * Promote deployment to another environment
 */
export async function promoteDeployment(
  id: string,
  targetEnvironmentId: string
): Promise<DeploymentResponse> {
  return api.post<DeploymentResponse>(`${DEPLOYMENTS_BASE}/${id}/promote`, {
    target_environment_id: targetEnvironmentId,
  });
}

/**
 * Get deployment diff
 */
export async function getDeploymentDiff(
  id: string,
  compareToVersion?: number
): Promise<DeploymentDiffResponse> {
  const params: Record<string, string> = {};
  if (compareToVersion !== undefined) {
    params.compare_to = String(compareToVersion);
  }
  return api.get<DeploymentDiffResponse>(`${DEPLOYMENTS_BASE}/${id}/diff`, { params });
}

/**
 * Get deployment history
 */
export async function getDeploymentHistory(id: string): Promise<DeploymentHistoryResponse> {
  return api.get<DeploymentHistoryResponse>(`${DEPLOYMENTS_BASE}/${id}/history`);
}

// ============================================
// Export all functions
// ============================================

export default {
  // Environments
  listEnvironments,
  getEnvironment,
  createEnvironment,
  updateEnvironment,
  deleteEnvironment,
  // Secrets
  listSecrets,
  setSecret,
  deleteSecret,
  // Deployments
  listDeployments,
  getDeployment,
  createDeployment,
  approveDeployment,
  rejectDeployment,
  rollbackDeployment,
  promoteDeployment,
  getDeploymentDiff,
  getDeploymentHistory,
};
